import { memo } from 'react';
import { Flex, Text } from '@chakra-ui/react';

import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_SEGMENT_ACTIVE_SHADOW,
  PLAN_SEGMENT_TRACK_BG,
  PLAN_TEXT,
  accent,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

export const PLAN_TIME_FILTERS = ['M', 'Q', 'Y'] as const;
export type PlanTimeFilterValue = typeof PLAN_TIME_FILTERS[number];

interface Properties {
  value: PlanTimeFilterValue;
  onChange: (value: PlanTimeFilterValue) => void;
}

/**
 * M/Q/Y time-aggregation control — one bordered pill (`PLAN_SEGMENT_TRACK_BG`
 * track) with segments inside, not three separately-bordered squares. Three
 * individually-chunky bordered buttons read as three unrelated controls
 * sitting next to each other; a single track with an active segment reads as
 * one control with three states, which is what M/Q/Y actually is.
 */
const PlanTimeFilterSegment = ({ value, onChange }: Properties) => (
  <Flex
    alignItems='center'
    bg={PLAN_SEGMENT_TRACK_BG}
    border='1px solid'
    borderColor={neutral.border}
    borderRadius={PLAN_REVAMP_RADIUS_SM}
    p='2px'
    gap='2px'
    flexShrink={0}
  >
    {PLAN_TIME_FILTERS.map((filterKey) => {
      const isActive = value === filterKey;

      return (
        <Flex
          key={filterKey}
          as='button'
          type='button'
          alignItems='center'
          justifyContent='center'
          minW='28px'
          h='26px'
          px='2px'
          borderRadius='6px'
          bg={isActive ? neutral.surface : 'transparent'}
          boxShadow={isActive ? PLAN_SEGMENT_ACTIVE_SHADOW : 'none'}
          cursor='pointer'
          transition='background 0.15s ease, box-shadow 0.15s ease'
          _hover={isActive ? undefined : { bg: 'rgba(255,255,255,0.6)' }}
          onClick={() => onChange(filterKey)}
        >
          <Text
            {...PLAN_TEXT.labelSmMedium}
            color={isActive ? accent.solid : textToken.secondary}
            fontWeight={isActive ? 700 : 500}
            lineHeight='1'
          >
            {filterKey}
          </Text>
        </Flex>
      );
    })}
  </Flex>
);

export default memo(PlanTimeFilterSegment);
